<template>
  <transition name="fade" mode="out-in">
    <div class="container" v-if="state.ready && !isPendingCampaign && !!campaign" :key="campaign?.id">
      <Header :name="campaign.campaign.internal_name" back="campaign-selector"></Header>
      <Toast v-if="campaign.archived" :text="$t('app.campaign.single.archivedToast')" color="red" icon="bi-trash" style="max-width: unset" :closable="false" />
      <div v-if="isTalentPanelOpen" class="result-wrapper">
        <TalentTabs :openedTalents="openedTalents" @openedTalents="newOpenedTalents" @selectedTalent="selectedTalent"></TalentTabs>
        <TalentPanel :talent="state.selectedTalent" @closeTalentPanel="appStore.talentResultPanelIsOpen = false"></TalentPanel>
      </div>
      <ResultFilters v-else-if="state.resultFiltersIsOpen" :appliedFilters="state.resultFilters" @applyFilters="updateFilters" @closeFilters="state.resultFiltersIsOpen = false"></ResultFilters>
      <div class="page-content-wrapper" v-else>
        <transition mode="out-in" name="fade">
          <Overview v-if="currentTab === 'Overview'" :search="state.search"></Overview>
          <Tracking v-else-if="currentTab === 'Tracking'" :search="state.search"></Tracking>
          <Results v-else-if="currentTab === 'Results'" :search="state.search" :filters="state.resultFilters" @openSelectedResults="openSelectedResults" @openResultFilters="state.resultFiltersIsOpen = true"></Results>
        </transition>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.result-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
}

.page-content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
}
</style>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { reactive, onMounted } from "vue";
import { useAppStore } from "@/stores/app.js";
import { useRoute } from "vue-router";


import Header from "@/components/Header.vue";
import Overview from "./partials/tables/Overview.vue";
import Tracking from "./partials/tables/Tracking.vue";
import Results from "./partials/tables/Results.vue";
import TalentPanel from "./partials/results/TalentPanel.vue";
import TalentTabs from "./partials/results/TalentTabs.vue";
import ResultFilters from "./partials/results/ResultsFilters.vue";

import { useCampaign } from "@/queries/campaigns.query";
import { useRoles } from "@/queries/user.query.js";

import { usePermissions } from "@/composables/permissions";
import { MemberRole } from "@/types/role.js";

const route = useRoute();

// Init stores
const appStore = useAppStore();
const { openedCandidateResults } = storeToRefs(appStore);
const { data: campaign, isPending: isPendingCampaign } = useCampaign();

const currentTab = computed(() => {
  return appStore.currentCampaignTab;
})

const state = reactive({
  fullScreen: false,
  onlyResults: false,
  resultFiltersIsOpen: false,
  selectedTalent: undefined,
  resultFilters: undefined,
  search: {
    value: "",
    searchable: [
      {
        combined: true,
        keys: ["pending_first_name", "pending_last_name"],
      },
      "pending_email",
      {
        combined: true,
        keys: ["candidate.first_name", "candidate.last_name"],
      },
      "candidate.email",
    ],
  },
  tabs: markRaw({ Overview, Tracking, Results }),
  paramsTab: appStore.campaignParametersTab,
  ready: true,
});

function updateFilters(filters) {
  state.resultFilters = filters;
}

function openSelectedResults(selectedResults) {
  appStore.openedCandidateResults = selectedResults;
  appStore.talentResultPanelIsOpen = true
}

function closePanelAndFilters() {
  appStore.talentResultPanelIsOpen = false;
  state.resultFiltersIsOpen = false;
}

function newOpenedTalents(talents) {
  appStore.openedCandidateResults = talents;

  if (appStore.openedCandidateResults.length == 0) {
    appStore.talentResultPanelIsOpen = false;
  }
}

function selectedTalent(selectedTalentParticipantId) {
  state.selectedTalent = openedCandidateResults.value.find((t) => t.participant_id == selectedTalentParticipantId);
}

const isTalentPanelOpen = computed(() => appStore.talentResultPanelIsOpen);
const openedTalents = computed(() => appStore.openedCandidateResults);

onMounted(async () => {
  if (Object.keys(state.tabs).includes(route.query.tab as string)) {
    appStore.currentCampaignTab = route.query.tab as string;
  }
});

</script>
